import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save } from "lucide-react";
import { useProfileForm } from "../hooks/profile.context";
import { useId } from "react";

interface ProfileFormProps {
	onSave?: () => void;
}

/**
 * Profile form component for updating user information.
 *
 * This component allows users to update their display name and email address.
 * Changes to these fields will require the user to log in again.
 *
 * @param onSave - Optional callback function to be called after successful save
 */
export const ProfileForm = ({ onSave }: ProfileFormProps) => {
	const {
		formData,
		isLoading,
		showSuccessMessage,
		handleChange,
		handleSubmit,
	} = useProfileForm({ onSave });

	const displayNameId = useId();

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			{/* Display Name Field */}
			<FormField
				id={displayNameId}
				name="displayName"
				label="Name"
				type="text"
				value={formData.displayName}
				onChange={handleChange}
				disabled={isLoading}
			/>

			{/* Submit Button */}
			<Button type="submit" className="w-full" size="lg" disabled={isLoading}>
				{isLoading ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin mr-2" />
						Speichern...
					</>
				) : (
					<>
						<Save className="h-4 w-4 mr-2" />
						Änderungen speichern
					</>
				)}
			</Button>

			{/* Success Message */}
			{showSuccessMessage && (
				<div className="text-center text-sm text-green-600">
					Deine Profildaten wurden erfolgreich aktualisiert.
				</div>
			)}
		</form>
	);
};

/**
 * Reusable form field component
 */
const FormField = ({
	id,
	name,
	label,
	type,
	value,
	onChange,
	disabled,
}: {
	id: string;
	name: string;
	label: string;
	type: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	disabled: boolean;
}) => (
	<div className="space-y-2">
		<label className="text-sm text-muted-foreground" htmlFor={id}>
			{label}
		</label>
		<Input
			id={id}
			name={name}
			type={type}
			value={value}
			onChange={onChange}
			placeholder={label}
			className="w-full"
			disabled={disabled}
		/>
	</div>
);
